import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { productsService } from 'services/firestore';
import logger from 'utils/logger';

const ProductContext = createContext();

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
};

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        // Fetch products from Firestore instead of JSON file
        const fetchedProducts = await productsService.getProducts();
        setProducts(fetchedProducts);
        setError(null);
      } catch (err) {
        logger.error('Error loading products from Firestore:', err);
        setError(err.message);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, []);

  // Accepts a numeric id OR a slug. ProductCard links to /products/<slug>
  // when a slug exists (preferred URL), so the lookup has to resolve both
  // shapes. parseInt-then-find-by-id is tried first to preserve the legacy
  // numeric path; a slug match closes the gap that BUG-20260529-01 surfaced.
  const getProductById = useCallback((idOrSlug) => {
    if (idOrSlug == null || idOrSlug === '') return undefined;
    const numeric = parseInt(idOrSlug, 10);
    if (!Number.isNaN(numeric)) {
      const byId = products.find((p) => p.id === numeric);
      if (byId) return byId;
    }
    return products.find((p) => p.slug === String(idOrSlug));
  }, [products]);

  const getProductsByCategory = useCallback((category) => {
    if (!category || category === 'all') {
      return products;
    }
    return products.filter(product => product.category === category);
  }, [products]);

  const getCategories = useCallback(() => {
    const categories = new Set(products.map(product => product.category));
    return Array.from(categories);
  }, [products]);

  const value = useMemo(() => ({
    products,
    loading,
    error,
    getProductById,
    getProductsByCategory,
    getCategories
  }), [products, loading, error, getProductById, getProductsByCategory, getCategories]);

  return (
    <ProductContext.Provider value={value}>
      {children}
    </ProductContext.Provider>
  );
};

